import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { LoginHeader, Icon, Select } from '@app/components';
import {
  UserRoleErrorMessage,
  Container,
  Row,
  InnerContainer,
  RowItem,
  Heading,
  Footer,
  ButtonContainer,
  CancelButton,
  Terms,
  ErrorMessage,
  Rule,
  SavingIndicator,
  Item,
  CustomLabel,
  UserRole,
} from '@app/modules/sign-up/signup-components';
import Input from '@app/components/input';
import Password from '@app/components/password';
import Button from '@app/components/button';
import Link from '@app/components/link';
import Uploader from '@app/components/uploader';
import { useLocation } from '@app/utils';
import {
  createUser as createUserFB,
  getToken,
  signIn,
} from '@app/auth/firebase';
import {
  useAddOrRemoveToken,
  useCreateUserMutation,
  useGetSignupRoles,
} from '@app/graphql';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { primaryColor } from '@app/styles';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { AuthContext } from '@app/providers';
import { getDefaultUrl } from '@app/routes/routes-helper';

type UserTypes = {
  name: string;
  key: string;
  userTypeId: string;
  status: string;
  _id: string;
};

const defaultSelectOption = {
  label: 'Select...',
  value: '',
};
export default function SignUp() {
  const { navigate } = useLocation();
  const { userInfo } = React.useContext(AuthContext);
  const defaultUrl: string = getDefaultUrl(userInfo);
  const emailVerified =
    userInfo &&
    userInfo.emailVerification &&
    userInfo.emailVerification.completed;

  React.useEffect(() => {
    if (userInfo) {
      if (emailVerified) {
        navigate(defaultUrl);
      } else {
        navigate('/email-verify');
      }
    }
    // eslint-disable-next-line
  }, [userInfo, navigate, emailVerified]);

  const { register, handleSubmit, watch, errors, formState } = useForm();
  const { isSubmitted } = formState;
  const [showOtherRole, setShowOtherRole] = React.useState(false);
  const { loading, data } = useGetSignupRoles();
  const [userRoles, setUserRoles] = React.useState([]);
  const [createUserDB] = useCreateUserMutation();
  const [fileName, setFileName] = React.useState('');
  const [profileImage, setProfileImage] = React.useState('');
  const [emailError, setEmailError] = React.useState('');
  const [sizeError, setSizeError] = React.useState('');
  const [saving, updateSaving] = React.useState(false);
  const [otherData, setOtherData] = React.useState({
    phone: '',
    userRole: defaultSelectOption,
    userTypeId: '',
  });
  const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
  const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d.*)[a-zA-Z0-9\S]{6,20}$/;
  const sizeLimit = 5 * 1024 * 1024;
  const [addToken] = useAddOrRemoveToken();

  useEffect(() => {
    if (!loading && data) {
      const { getUserSignUpRoles } = data;
      if (getUserSignUpRoles) {
        const { success, userRoles } = getUserSignUpRoles;
        if (success && userRoles && userRoles.length) {
          setTimeout(() => {
            setUserRoles(
              userRoles
                .map((userRole: UserTypes) => {
                  return {
                    ...userRole,
                    label: userRole.name,
                    value: userRole._id,
                  };
                })
                .concat([
                  {
                    name: 'Other',
                    label: 'Other',
                    value: '61d80b3a2224a8cd0df30225',
                    _id: '61d80b3a2224a8cd0df30225',
                    userTypeId: userRoles[0].userTypeId,
                  },
                ]),
            );
          }, 200);
        }
      }
    }
  }, [loading, data]);

  /**
   *
   * @param event onchange event
   * @returns profile pic changes
   */
  function onProfileImageChange(event: any) {
    if (event.target.files && event.target.files[0]) {
      if (event.target.files[0].size > sizeLimit) {
        setSizeError('Maximum size limit is 5 Mb');
        return false;
      }
      setFileName(event.target.files[0].name);

      const reader = new FileReader();

      reader.onload = (e: any) => {
        setProfileImage(e.target.result);
      };
      reader.readAsDataURL(event.target.files[0]);
    }
  }

  const setToken = async (currentUser: any, isGoogleSignIn?: boolean) => {
    const token: any = await getToken(currentUser);
    const params: any = {
      firebaseId: currentUser.uid,
      token: token.token,
      expiryDateTime: token.expiryDateTime.toString(),
      action: 'ADD',
    };
    await addToken({
      variables: params,
    })
      .then((result: any) => {})
      .catch((error: any) => {});
  };

  /**
   *
   * @param data submitted data of form
   */
  async function onSubmit(data: any) {
    if (otherData && otherData.userRole && !otherData.userRole.value) {
      return;
    }
    updateSaving(true);
    const currentUser = await createUserFB(data)
      .then()
      .catch((error: any) => {
        updateSaving(false);
        if (error.code === 'auth/email-already-in-use') {
          setEmailError('Email already exists');
        }
      });

    if (currentUser) {
      updateSaving(false);
      const { uid } = currentUser;
      let params = {
        action: 'add',
        source: 'EMAIL',
        firebaseId: uid,
        phone: otherData.phone,
        userRoleId: otherData.userRole.value,
        userTypeId: otherData.userTypeId,
        profilePicUrl: profileImage,
        ...data,
      };
      if (otherData && otherData.userRole.label === 'Other') {
        Object.assign(params, {
          otherRoleData: data.otherRoleData || '',
        });
      }
      createUserDB({
        variables: params,
      })
        .then(async (res: any) => {
          await setToken(currentUser).then(() => {
            const {
              createUser: { emailVerification },
            } = res.data;
            const { completed = false } = emailVerification;
            if (!completed) {
              signIn(data.email, data.password);
              navigate('/email-verify');
            }
          });
        })
        .catch((error: any) => {
          console.log(error);
        });
    }
  }

  function onChange(data: any) {
    if (data.target.name === 'email' && emailError) {
      setEmailError('');
    }
  }

  /**
   *
   * @param e event
   */
  function onDataChange(e: any, type: string) {
    if (type === 'phone') {
      setOtherData({ ...otherData, phone: e });
    }
    if (type === 'userRole') {
      if (e && e.label === 'Other') {
        setShowOtherRole(true);
      } else {
        setShowOtherRole(false);
      }
      setOtherData({
        ...otherData,
        userRole: e,
        userTypeId: e && e.userTypeId ? e.userTypeId : '',
      });
    }
  }

  return (
    <>
      <LoginHeader />
      <Container>
        <InnerContainer>
          <Heading>Sign Up</Heading>
          <form
            onSubmit={handleSubmit(onSubmit)}
            autoComplete="off"
            onChange={onChange}>
            <Row>
              <RowItem>
                <Uploader
                  onChange={e => onProfileImageChange(e)}
                  fileName={fileName}
                  profileImage={profileImage}
                  label="Your Profile Pic"
                />
                {sizeError && <ErrorMessage>{sizeError}</ErrorMessage>}
              </RowItem>
            </Row>
            <Row>
              <RowItem>
                <Input
                  label="First Name"
                  star="*"
                  placeholder="Enter First Name"
                  name="firstName"
                  register={register({ required: true })}
                />
                {errors.firstName && (
                  <ErrorMessage>Please enter First Name</ErrorMessage>
                )}
              </RowItem>
              <RowItem>
                <Input
                  star="*"
                  label="Last Name"
                  placeholder="Enter Last Name"
                  name="lastName"
                  register={register({ required: true })}
                />
                {errors.lastName && (
                  <ErrorMessage>Please enter Last Name</ErrorMessage>
                )}
              </RowItem>
            </Row>
            <Row>
              <RowItem>
                <Input
                  star="*"
                  type="email"
                  label="Email"
                  placeholder="Enter Email"
                  name="email"
                  register={register({
                    required: true,
                    pattern: emailPattern,
                  })}
                />
                {errors.email && errors.email.type === 'required' && (
                  <ErrorMessage>Please enter Email</ErrorMessage>
                )}
                {errors.email && errors.email.type === 'pattern' && (
                  <ErrorMessage>Please enter valid Email</ErrorMessage>
                )}
                {!errors.email && emailError && (
                  <ErrorMessage>{emailError}</ErrorMessage>
                )}
              </RowItem>
              <RowItem>
                <Input
                  type="phone_number"
                  label="Phone"
                  placeholder="Enter Phone Number"
                  name="phone"
                  value={otherData.phone}
                  onChange={(e: any) => onDataChange(e, 'phone')}
                />
              </RowItem>
            </Row>
            <Row>
              <UserRole>
                <CustomLabel>What is your role here?</CustomLabel>
                <Select
                  options={userRoles}
                  value={otherData.userRole}
                  name="userRole"
                  onChange={(e: any) => onDataChange(e, 'userRole')}
                  register={register({ required: true })}
                />
                {isSubmitted &&
                otherData.userRole &&
                !otherData.userRole.value ? (
                  <UserRoleErrorMessage>
                    Please Select User Role
                  </UserRoleErrorMessage>
                ) : null}
              </UserRole>
              {showOtherRole ? (
                <RowItem>
                  <Input
                    star="*"
                    label="User Role"
                    placeholder="Enter User Role"
                    name="otherRoleData"
                    register={register({
                      required: showOtherRole ? true : false,
                    })}
                  />
                  {errors && errors.otherRoleData && (
                    <ErrorMessage>Please enter user role</ErrorMessage>
                  )}
                </RowItem>
              ) : null}
            </Row>
            <Row>
              <Item>
                <Input
                  label="Company"
                  placeholder="Enter Company Name"
                  name="company"
                  register={register()}
                />
              </Item>
            </Row>
            <Row>
              <RowItem>
                <Password
                  label="Password"
                  placeholder="Enter Password"
                  name="password"
                  register={register({
                    required: true,
                    pattern: passwordPattern,
                  })}
                />
                {errors.password && errors.password.type === 'required' && (
                  <ErrorMessage>Please enter Password</ErrorMessage>
                )}
                {errors.password && errors.password.type === 'pattern' && (
                  <ErrorMessage>Please enter a strong Password</ErrorMessage>
                )}
              </RowItem>
              <RowItem>
                <Password
                  label="Confirm Password"
                  placeholder="Confirm Password"
                  name="confirmPassword"
                  register={register({
                    required: true,
                    validate: value => {
                      return value === watch('password');
                    },
                  })}
                />
                {errors.confirmPassword &&
                  errors.confirmPassword.type === 'required' && (
                    <ErrorMessage>Please confirm Password</ErrorMessage>
                  )}
                {errors.confirmPassword &&
                  errors.confirmPassword.type === 'validate' && (
                    <ErrorMessage>Passwords did not match</ErrorMessage>
                  )}
              </RowItem>
            </Row>
            <Rule>
              <Icon name="info" />A strong Password contain at least 6
              alphanumaric Characters, and includes uppercase & lowercase
            </Rule>
            <Terms>
              By creating an account, I agree to the{' '}
              <Link variant="primary" href="/">
                Terms of Use
              </Link>{' '}
              and acknowledge that i have read the{' '}
              <Link href="/" variant="primary">
                Privacy Policy
              </Link>
            </Terms>
            <Footer>
              {saving && (
                <SavingIndicator>
                  <FontAwesomeIcon
                    color={primaryColor}
                    icon={faCircleNotch}
                    size="lg"
                    spin
                  />
                </SavingIndicator>
              )}
              <CancelButton>
                <Button
                  variant="text"
                  size="large"
                  onClick={() => navigate('/')}>
                  Cancel
                </Button>
              </CancelButton>
              <ButtonContainer disabled={saving}>
                <Button
                  variant="primary"
                  size="large"
                  width="100%"
                  type="submit">
                  Sign Up
                </Button>
              </ButtonContainer>
            </Footer>
          </form>
        </InnerContainer>
      </Container>
    </>
  );
}
